import { useTranslation } from '@/hooks/useTranslation';
import { useAlbumNavigation } from '@/hooks/useAlbumNavigation';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useMetaTags } from '@/hooks/useMetaTags';
import PageHeader from '@/components/PageHeader';
import { Upload, X, CloudUpload, ImageUp } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/hooks/useSettings';
import { useBanStatus } from '@/hooks/useBanStatus';
import { CLOUDINARY_UPLOAD_URL, CLOUDINARY_PRESET_NAME, CLOUDINARY_FOLDER_USER_UPLOADS, MAX_MEDIA_DESCRIPTION_LENGTH } from "@/lib/constants";
import CircularProgressSpinner from '@/components/CircularProgressSpinner';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from '@/contexts/AlbumProvider';
// import uploadImg from '@/assets/upload-img.png';
import * as Sentry from "@sentry/react";
import { safeLogRocketTrack } from '@/lib/utils';
import {
  buildCloudinaryFormData,
  createPendingWatcher,
  generateLocalThumbnail,
  generatePublicId,
  getUnsupportedUploadReason,
  insertInitialMediaRow,
  isSupportedUploadImage,
  uploadToCloudinary,
} from '@/lib/utils-upload';

interface FileWithPreview {
  file: File;
  preview: string; // blob or data URL (thumbnail)
  id: string;
  description: string; // NEW: description for this file
}

const UPLOAD_ACCEPTED_TYPES =
  '.jpg,.jpeg,.png,.webp,.avif,.heic,.heif,image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif';

const UploadPage = () => {
  const { albumId, albumSlug } = useAlbumContext();
  const { getAlbumPath } = useAlbumNavigation();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isMessageFading, setIsMessageFading] = useState(false);
  const [filesTotal, setFilesTotal] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  // Logical progress step to avoid visual resets: combines real and fake steps
  const [logicalStep, setLogicalStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getBooleanSetting } = useSettings();
  const { isBanned } = useBanStatus();
  const { t } = useTranslation();
  const enableUploads = getBooleanSetting('enable_uploads', true);
  const enableSingleImageDescription = getBooleanSetting('enable_single_image_description', false);

  useMetaTags({
    title: t('uploadPageMetaTitle'),
    pathname: '/subir'
  });

  // Fake steps logic to improve UX for small file counts
  const computeFakeSteps = useCallback((count: number) => {
    if (count <= 0) return 0;
    const base = count === 1 ? 5
      : count === 2 ? 3
        : count === 3 ? 2
          : (count === 4 || count === 5) ? 1
            : 0;
    const perFileExtras = count >= 2 ? count : 0;
    return base + perFileExtras;
  }, []);

  const fakeStepsMax = useMemo(() => computeFakeSteps(filesTotal), [filesTotal, computeFakeSteps]);

  // Calculate upload percentage
  // - Uses logical steps = real (filesUploaded) + fake (fakeStepsCount)
  // - Reaches 100% one step before completion and stays at 100% for the last two steps
  const uploadPercentage = useMemo(() => {
    if (filesTotal <= 0) return 0;
    const total = filesTotal + fakeStepsMax;
    if (logicalStep >= total - 1) return 100; // keep 100% for the last two steps
    const pct = Math.round((logicalStep / total) * 100);
    return Math.min(99, Math.max(0, pct));
  }, [filesTotal, fakeStepsMax, logicalStep]);

  // Set name from user data when available
  useEffect(() => {
    if (user?.user_metadata?.name) {
      // Extract only the first name
      const firstName = user.user_metadata.name.split(' ')[0];
      setName(firstName);
    }
  }, [user]);

  const getUploadMessages = useCallback((files: FileWithPreview[]) => {
    return ([
      t('uploadProgressOptimizing', { count: files.length }),
      t('uploadProgressPositioning', { count: files.length }),
      t('uploadProgressAnalyzing', { name: name || t('uploadNameFallbackWow') }),
      t('uploadProgressProcessing', { count: files.length }),
      t('uploadProgressAlmostThere', { name: name || '' }),
      t('uploadProgressConnecting', { count: files.length }),
      t('uploadProgressGold', { name: name || t('uploadNameFallbackTip') }),
      t('uploadProgressExcitement', { name: name || '' }),
      t('uploadProgressFinishing', { count: files.length })
    ])
  }, [name, t]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    // Only start rotating messages once progress has reached 100%
    if (isUploading && uploadPercentage >= 100) {
      interval = setInterval(() => {
        setIsMessageFading(true);
        setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % getUploadMessages(files).length);
          setIsMessageFading(false);
        }, 500);
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isUploading, uploadPercentage, files, getUploadMessages]);

  const getRejectedFilesMessage = useCallback((rejectedCount: number) => {
    return `${t('uploadErrorOnlyImagesNoGif')} ${t('uploadErrorSomeFilesRejected', { count: rejectedCount })}`;
  }, [t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const raw = Array.from(e.target.files);
    const validFiles = raw.filter((file) => isSupportedUploadImage(file));
    const rejectedCount = raw.length - validFiles.length;

    if (rejectedCount > 0) {
      setErrorMessage(getRejectedFilesMessage(rejectedCount));
      setShowErrorDialog(true);
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    const appended = validFiles.map((file) => ({
      file,
      preview: '', // generate asynchronously
      id: Math.random().toString(36).substring(7),
      description: '' // NEW: initialize empty description
    }));
    // Append placeholders immediately
    setFiles(prev => [...prev, ...appended]);
    // Generate thumbnails async
    appended.forEach(async (f) => {
      try {
        const thumbUrl = await generateLocalThumbnail(f.file, 120);
        setFiles(prev => prev.map(x => x.id === f.id ? { ...x, preview: thumbUrl } : x));
      } catch {
        // fallback to original object URL
        const url = URL.createObjectURL(f.file);
        setFiles(prev => prev.map(x => x.id === f.id ? { ...x, preview: url } : x));
      }
    });
    e.target.value = '';
  };

  const removeFile = (indexToRemove: number) => {
    const fileToRemove = files[indexToRemove];
    if (fileToRemove) {
      // Clean up the object URL to prevent memory leaks
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const updateDescription = (index: number, description: string) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, description } : f));
  };

  const clearForm = () => {
    // Clean up all object URLs
    files.forEach(fileData => {
      if (fileData.preview && fileData.preview.startsWith('blob:')) {
        URL.revokeObjectURL(fileData.preview);
      }
    });
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      files.forEach(fileData => {
        if (fileData.preview && fileData.preview.startsWith('blob:')) {
          URL.revokeObjectURL(fileData.preview);
        }
      });
    };
  }, []);

  // Safety: make sure class is removed if component unmounts unexpectedly
  useEffect(() => {
    return () => { try { document.body.classList.remove('hide-floating-nav'); } catch { } };
  }, []);

  // Realtime/polling watcher will be created on submit
  const watcherRef = useRef<ReturnType<typeof createPendingWatcher> | null>(null);
  const totalStepsRef = useRef(0);
  const driverTimerRef = useRef<number | null>(null);


  useEffect(() => {
    return () => {
      if (watcherRef.current) watcherRef.current.cleanup();
      watcherRef.current = null;
      if (driverTimerRef.current) {
        clearInterval(driverTimerRef.current);
        driverTimerRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (files.length === 0) {
      setErrorMessage(t('uploadErrorSelectAtLeastOne'));
      setShowErrorDialog(true);
      return;
    }

    if (!user?.id) {
      setErrorMessage(t('uploadErrorSignInRequired'));
      setShowErrorDialog(true);
      return;
    }

    const invalidFiles = files.filter((fileData) => getUnsupportedUploadReason(fileData.file) !== null);
    if (invalidFiles.length > 0) {
      setErrorMessage(getRejectedFilesMessage(invalidFiles.length));
      setShowErrorDialog(true);
      return;
    }

    // Check if user is banned before allowing upload
    if (isBanned) {
      setErrorMessage(t('uploadErrorSuspended'));
      setShowErrorDialog(true);
      return;
    }

    // Reset rotating messages to the first one for a fresh upload session
    setCurrentMessageIndex(0);
    setIsMessageFading(false);

    setIsUploading(true);
    // Hide FloatingNav while uploading overlay is visible
    try { document.body.classList.add('hide-floating-nav'); } catch { }
    setFilesTotal(files.length);
    setFilesUploaded(0);
    setLogicalStep(0);
    // clear any previous driver
    if (driverTimerRef.current) {
      clearInterval(driverTimerRef.current);
      driverTimerRef.current = null;
    }

    safeLogRocketTrack("Empezando subida...", {
      user_id: user.id,
      album_name: albumSlug || '',
      cantidad: files?.length || 0
    });

    try {
      // Create watcher and (attempt to) enable realtime just-in-time
      watcherRef.current = createPendingWatcher(supabase as any, albumId || undefined);
      const enabled = await watcherRef.current.ensureReady();
      console.info('[Realtime] ready:', enabled);

      // Snapshot total steps for this session and start driver
      const fakeMax = computeFakeSteps(files.length);
      totalStepsRef.current = files.length + fakeMax;
      // Initial kick + paced increments
      setLogicalStep((prev) => Math.min(prev + 1, Math.max(0, totalStepsRef.current - 1)));
      driverTimerRef.current = window.setInterval(() => {
        setLogicalStep((prev) => {
          if (prev >= totalStepsRef.current - 1) return prev; // hold near 100%
          return Math.min(prev + 1, totalStepsRef.current - 1);
        });
      }, 500);

      // Run uploads in parallel per file
      const tasks = files.map(async (fileData) => {
        const { folderPath, publicId, fullPublicId } = generatePublicId(albumSlug, albumId as string);
        const formData = buildCloudinaryFormData({
          file: fileData.file,
          folderPath,
          userId: user.id,
          publicId,
          uploadPreset: CLOUDINARY_PRESET_NAME,
        });

        // Start initial pending row insert in parallel
        const insertPromise = insertInitialMediaRow(supabase as any, {
          albumId: albumId as string,
          userId: user.id,
          fullPublicId,
          description: fileData.description || undefined,
        }).catch((err) => {
          console.warn('Initial media insert failed:', err);
          Sentry.captureException(err, { extra: { context: 'Initial media insert (pending row)' } });
        });

        const uploadData = await uploadToCloudinary(CLOUDINARY_UPLOAD_URL, formData).catch((err) => {
          Sentry.captureException(err, { extra: { context: 'Error al subir a Cloudinary' } });
          throw err;
        });
        console.log("DEBUG 🟠: Cloudinary done ", uploadData);

        await insertPromise;
        // Tracking
        safeLogRocketTrack("Foto Subida", {
          user_id: user.id,
          album_name: albumSlug || ''
        });

        // Wait until this media row has pending=false before counting it as completed
        if (watcherRef.current) {
          await watcherRef.current.waitUntilFalse(fullPublicId);
        }
        setFilesUploaded(prev => prev + 1);
        // Nudge logical progress on each real completion to keep motion
        setLogicalStep((prev) => Math.min(prev + 1, totalStepsRef.current));
      });

      await Promise.all(tasks);

      // Ensure visual progress reaches 100% before closing overlay
      setFilesUploaded(filesTotal);
      setLogicalStep(totalStepsRef.current);
      if (driverTimerRef.current) {
        clearInterval(driverTimerRef.current);
        driverTimerRef.current = null;
      }

      setShowSuccessDialog(true);
      setIsUploading(false);
      try { document.body.classList.remove('hide-floating-nav'); } catch { }
      setFilesTotal(0);
      setFilesUploaded(0);
      setLogicalStep(0);
      clearForm();
      if (watcherRef.current) watcherRef.current.cleanup();
      watcherRef.current = null;
    } catch (error) {
      try { document.body.classList.remove('hide-floating-nav'); } catch { }
      Sentry.captureMessage(error instanceof Error ? error.message : String(error), "error");
      console.error('Upload error:', error);
      setErrorMessage(t('uploadErrorGeneric'));
      setShowErrorDialog(true);
      setIsUploading(false);
      setFilesTotal(0);
      setFilesUploaded(0);
    }
  };

  return (
    <>
      <div className='min-h-screen bg-background mb-14'>
        <PageHeader title={t('uploadPageHeaderTitle')} />

        <div className='max-w-md mx-auto px-4 py-8'>
          {!enableUploads ? (
            <div className='text-center py-16'>
              <div className='m-auto flex justify-center w-full py-6'>
                <ImageUp className='w-28 h-28' />
              </div>
              <p className='text-muted-foreground'>{t('uploadsComingSoon')}</p>
            </div>
          ) : (
            <Card className='border-border'>
              <CardHeader className='text-center mb-5'>
                {files.length === 0 && (
                  <CardTitle
                    className='text-2xl font-light leading-none text-foreground mb-5'
                    style={{ paddingTop: 'var(--heading-top-offset, 0px)' }}
                  >
                    {t('shareYourPhotos')}
                  </CardTitle>
                )}
                {files.length === 0 ? (
                  <div className='m-auto block py-6'>
                    <CloudUpload className='w-28 h-28' />
                  </div>
                ) : (
                  <div className='m-auto block pt-3 pb-0 -mb-4'>
                    <CloudUpload className='w-12 h-12' />
                  </div>
                )}
                {files.length === 0 && (
                  <p className='text-muted-foreground text-sm'>
                    {t('chooseBestPhotos')}
                  </p>
                )}
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className='space-y-6 mb-10'>
                  {files.length === 0 ? (
                    /* No files selected - show Seleccionar button */
                    <div className='relative'>
                      <input
                        ref={fileInputRef}
                        type='file'
                        multiple
                        accept={UPLOAD_ACCEPTED_TYPES}
                        onChange={handleFileChange}
                        className='hidden'
                      />
                      <Button
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        className='w-full h-12 bg-primary hover:bg-secondary text-primary-foreground font-medium rounded-lg transition-colors'
                      >
                        {t('select')}
                      </Button>
                    </div>
                  ) : (
                    /* Files selected - show Subir button first, then thumbnails, then Seleccionar */
                    <div className='space-y-4'>
                      {/* Upload button at the top */}
                      <Button
                        type='submit'
                        className='w-full h-12 bg-primary hover:bg-secondary text-primary-foreground font-medium rounded-lg transition-colors'
                        disabled={isUploading}
                      >
                        <Upload className='w-4 h-4 mr-2' />
                        {isUploading ? t('uploading') : t('uploadCountLabel', { count: files.length })}
                      </Button>

                      {/* Add more files button right below Upload */}
                      <div className='relative'>
                        <input
                          ref={fileInputRef}
                          type='file'
                          multiple
                          accept={UPLOAD_ACCEPTED_TYPES}
                          onChange={handleFileChange}
                          className='hidden'
                        />
                        <Button
                          type='button'
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          variant='outline'
                          className='w-full h-12 font-medium rounded-lg transition-colors'
                        >
                          {t('selectMore')}
                        </Button>
                      </div>

                      {/* File count and thumbnails */}
                      <div className='space-y-4'>
                        {/* When descriptions are enabled: keep existing vertical list with textarea */}
                        {enableSingleImageDescription ? (
                          <div className='space-y-3'>
                            {files.map((fileData, index) => (
                              <div
                                key={fileData.id}
                                className='flex gap-3 items-start'
                              >
                                {/* Thumbnail with remove button */}
                                <div
                                  className='relative rounded-lg overflow-hidden flex-shrink-0'
                                  style={{
                                    width: '120px',
                                    height: '120px'
                                  }}
                                >
                                  {fileData.file.type.startsWith('image/') ? (
                                    fileData.preview ? (
                                      <img
                                        src={fileData.preview}
                                        alt={fileData.file.name}
                                        className='w-full h-full object-cover rounded-lg'
                                        loading="eager"
                                        fetchPriority="high"
                                        style={{ width: '120px', height: '120px' }}
                                      />
                                    ) : (
                                      <div className='w-full h-full animate-pulse bg-muted rounded-lg' />
                                    )
                                  ) : (
                                    <div className='w-full h-full flex items-center justify-center bg-muted rounded-lg'>
                                      <Upload className='w-8 h-8 text-muted-foreground' />
                                    </div>
                                  )}
                                  <button
                                    type='button'
                                    onClick={() => removeFile(index)}
                                    disabled={isUploading}
                                    className={`absolute top-1 right-1 w-6 h-6 bg-destructive hover:bg-destructive/80 text-destructive-foreground rounded-full flex items-center justify-center transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                    style={{ display: isUploading ? 'none' : 'flex' }}
                                  >
                                    <X className='w-3 h-3' />
                                  </button>
                                </div>

                                {/* Description textarea */}
                                <div className='flex-1'>
                                  <Textarea
                                    placeholder={t('addDescription')}
                                    value={fileData.description}
                                    onChange={(e) => updateDescription(index, e.target.value)}
                                    disabled={isUploading}
                                    maxLength={MAX_MEDIA_DESCRIPTION_LENGTH}
                                    rows={5}
                                    className='resize-none text-base md:text-xs'
                                  />
                                  <p className='text-xs text-muted-foreground mt-1 text-right'>
                                    {fileData.description.length}/{MAX_MEDIA_DESCRIPTION_LENGTH}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          /* Descriptions disabled: show a 3-column grid of thumbnails only */
                          <div className='grid grid-cols-3 gap-3'>
                            {files.map((fileData, index) => (
                              <div
                                key={fileData.id}
                                className='relative rounded-lg overflow-hidden'
                                style={{ width: '120px', height: '120px' }}
                              >
                                {fileData.file.type.startsWith('image/') ? (
                                  fileData.preview ? (
                                    <img
                                      src={fileData.preview}
                                      alt={fileData.file.name}
                                      className='w-full h-full object-cover rounded-lg'
                                      loading="eager"
                                      fetchPriority="high"
                                      style={{ width: '120px', height: '120px' }}
                                    />
                                  ) : (
                                    <div className='w-full h-full animate-pulse bg-muted rounded-lg' />
                                  )
                                ) : (
                                  <div className='w-full h-full flex items-center justify-center bg-muted rounded-lg'>
                                    <Upload className='w-8 h-8 text-muted-foreground' />
                                  </div>
                                )}
                                <button
                                  type='button'
                                  onClick={() => removeFile(index)}
                                  disabled={isUploading}
                                  className={`absolute top-1 right-1 w-6 h-6 bg-destructive hover:bg-destructive/80 text-destructive-foreground rounded-full flex items-center justify-center transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  style={{ display: isUploading ? 'none' : 'flex' }}
                                >
                                  <X className='w-3 h-3' />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </form>

                <div className="border-t border-border mb-6"></div>

                <ul className="list-disc space-y-2 pl-4">
                  <li className='text-muted-foreground text-xs'>
                    {t('uploadRuleAuthorCanDelete')}
                  </li>
                  <li className='text-muted-foreground text-xs'>
                    {t('uploadRuleModeration')}
                  </li>
                </ul>

              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog}
        onOpenChange={(open) => {
          setShowSuccessDialog(open);
          if (!open) {
            clearForm();
          }
        }}
      >

        <DialogContent className='text-center flex flex-col justify-center items-center h-60 fixed z-50 max-w-xs'>
          <div className='absolute inset-0 pointer-events-none'>
            <div className='confetti-container'>
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className={`confetti confetti-${i + 1}`}></div>
              ))}
            </div>
          </div>

          <button
            onClick={() => {
              setShowSuccessDialog(false)
              clearForm();
            }}
            className='absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
          >
            <X className='h-4 w-4' />
            <span className='sr-only'>{t('close')}</span>
          </button>

            <div className='text-center space-y-6 p-6 relative z-10'>
              <div className='space-y-2'>
              <h2
                className='text-2xl font-semibold leading-none text-foreground'
                style={{ paddingTop: 'var(--heading-top-offset, 0px)' }}
              >
                {t('uploadSuccessTitle', { name })}
              </h2>
              <p className='text-md text-foreground'>{t('uploadSuccessSubtitle')}</p>
            </div>

            <div className='flex flex-col gap-3 '>
              <Button
                onClick={() => navigate(getAlbumPath('/album'))}
                className='w-full bg-primary hover:bg-secondary text-primary-foreground max-w-52 m-auto'
              >
                {t('viewAlbumAction')}
              </Button>
              <Button
                onClick={() => {
                  clearForm();
                  setShowSuccessDialog(false);
                }}
                variant='outline'
                className='w-full max-w-52 m-auto'
              >
                {t('uploadMore')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className='max-w-md mx-auto bg-background border-none shadow-2xl m-4'>
          <button
            onClick={() => setShowErrorDialog(false)}
            className='absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
          >
            <X className='h-4 w-4' />
            <span className='sr-only'>{t('close')}</span>
          </button>

          <div className='text-center space-y-4 p-6'>
            <div className='space-y-2'>
              <h2
                className='text-2xl font-semibold leading-none text-destructive'
                style={{ paddingTop: 'var(--heading-top-offset, 0px)' }}
              >
                {t('error')}
              </h2>
              <p className='text-muted-foreground'>{errorMessage}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay */}
      {isUploading && (
        <div className='fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4'>
          <div className='max-w-md w-full bg-background border-none shadow-2xl rounded-lg p-8 relative overflow-hidden' style={{ height: 'calc(100vh - 8rem)', maxHeight: '600px' }}>


            <div className='text-center flex flex-col justify-center items-center h-full relative z-10'>
              <div className='mb-6'>
                <CircularProgressSpinner percentage={uploadPercentage} totalFiles={filesTotal} />
              </div>

              <div className='min-h-[4rem] flex items-center justify-center px-4'>
                {uploadPercentage >= 100 && (
                  <p
                    className={`text-foreground font-medium text-lg leading-relaxed transition-all duration-500 ${isMessageFading ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'
                      }`}
                  >
                    {getUploadMessages(files)[currentMessageIndex]}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPage;
